import {
  useBusinessMypageProductList,
  useUpdateBusinessMypageProduct,
} from "~/services/business/myPage";
import { useState, useEffect, useCallback } from "react";
import { BusinessCompanyProduct, BusinessProductGroup } from "~/types/common";

const useProductManagementHotel = () => {
  const { data: productList, isLoading } = useBusinessMypageProductList(
    "PRODUCT_TYPE_HOTEL"
  );
  const updateProductMutation = useUpdateBusinessMypageProduct();

  // API 데이터 구조를 그대로 유지하면서 상태 관리
  const [modifiedProductList, setModifiedProductList] = useState<
    BusinessProductGroup[]
  >([]);

  // API 데이터로부터 초기 상태 설정 (깊은 복사)
  useEffect(() => {
    if (productList && productList.length > 0) {
      const deepCopyList = JSON.parse(
        JSON.stringify(productList)
      ) as BusinessProductGroup[];
      setModifiedProductList(deepCopyList);
    }
  }, [productList]);

  // 특정 상품의 활성화 상태 토글
  const handleToggle = useCallback((seq: number) => {
    setModifiedProductList((prev) =>
      prev.map((group) => ({
        ...group,
        products: group.products.map((product) => {
          if (product.seq === seq) {
            const newActiveYn = product.activeYn === "Y" ? "N" : "Y";
            return {
              ...product,
              activeYn: newActiveYn,
              amount: newActiveYn === "N" ? 0 : product.amount, // 비활성화 시 0
            };
          }
          return product;
        }),
      }))
    );
  }, []);

  // 특정 상품의 금액 변경
  const handleAmountChange = useCallback(
    (seq: number, amount: number) => {
      setModifiedProductList((prev) =>
        prev.map((group) => ({
          ...group,
          products: group.products.map((product) =>
            product.seq === seq
              ? { ...product, amount }
              : product
          ),
        }))
      );
    },
    []
  );

  // 입력 처리 (문자열 -> 숫자 변환)
  const handleInput = useCallback(
    (seq: number, val: string) => {
      const onlyNumber = val.replace(/[^0-9]/g, "");
      const amount = onlyNumber ? parseInt(onlyNumber, 10) : 0;
      handleAmountChange(seq, amount);
    },
    [handleAmountChange]
  );

  // 변경된 상품만 추출하는 함수
  const getChangedProducts = useCallback(() => {
    if (!productList || !modifiedProductList) return [];

    const changedProducts: BusinessCompanyProduct[] = [];
    
    console.log("=== 호텔링 변경된 상품 추적 ===");
    console.log("원본 상품 목록:", productList);
    console.log("수정된 상품 목록:", modifiedProductList);
    
    modifiedProductList.forEach((modifiedGroup) => {
      const originalGroup = productList.find(
        (g) => g.cdProductGroup === modifiedGroup.cdProductGroup
      );
      if (!originalGroup) {
        console.log(`원본 그룹을 찾을 수 없음: ${modifiedGroup.cdProductGroup}`);
        return;
      }

      modifiedGroup.products.forEach((modifiedProduct) => {
        const originalProduct = originalGroup.products.find(
          (p) => p.seq === modifiedProduct.seq
        );

        if (!originalProduct) {
          console.log(`원본 상품을 찾을 수 없음: ${modifiedProduct.seq}`);
          return;
        }

        const isActiveChanged = originalProduct.activeYn !== modifiedProduct.activeYn;
        const isAmountChanged = originalProduct.amount !== modifiedProduct.amount;

        if (isActiveChanged || isAmountChanged) {
          console.log(`변경된 호텔링 상품 발견:`, {
            productName: modifiedProduct.productName,
            seq: modifiedProduct.seq,
            originalActiveYn: originalProduct.activeYn,
            modifiedActiveYn: modifiedProduct.activeYn,
            originalAmount: originalProduct.amount,
            modifiedAmount: modifiedProduct.amount,
            changes: {
              activeYn: isActiveChanged,
              amount: isAmountChanged
            }
          });
          changedProducts.push(modifiedProduct);
        }
      });
    });

    console.log(`총 ${changedProducts.length}개의 호텔링 상품이 변경됨`);
    console.log("변경된 호텔링 상품 목록:", changedProducts);
    console.log("=== 호텔링 변경 추적 완료 ===");

    return changedProducts;
  }, [productList, modifiedProductList]);

  // 저장 함수
  const saveProducts = useCallback(async () => {
    const changedProducts = getChangedProducts();
    if (changedProducts.length === 0) {
      console.log("변경된 상품이 없습니다.");
      return { success: false, message: "변경된 상품이 없습니다." };
    }

    // activeYn: "Y"인데 amount 값이 없으면 activeYn: "N"으로 변경
    const validatedProductList = modifiedProductList.map((group) => ({
      ...group,
      products: group.products.map((product) => {
        if (
          product.activeYn === "Y" &&
          (!product.amount || product.amount === 0)
        ) {
          return {
            ...product,
            activeYn: "N" as const,
            amount: 0,
          };
        }
        return product;
      }),
    }));

    try {
      // 검증된 데이터를 서버로 전송
      await updateProductMutation.mutateAsync(validatedProductList);
      console.log("호텔링 상품 저장 성공:", validatedProductList);
      console.log("변경된 상품들:", changedProducts);
      return {
        success: true,
        message: "저장 성공",
        changedCount: changedProducts.length,
      };
    } catch (error) {
      console.error("호텔링 상품 저장 실패:", error);
      throw error;
    }
  }, [getChangedProducts, updateProductMutation, modifiedProductList]);

  return {
    productList: modifiedProductList,
    originalProductList: productList,
    isLoading,
    isSaving: updateProductMutation.isPending,
    handleToggle,
    handleInput,
    getChangedProducts,
    saveProducts,
  };
};

export default useProductManagementHotel; 